import { useEffect, useState } from 'react'
import { DEFAULT_BAND, type BandSetting, type BandSide } from '@shared/worker'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'
import { Switch } from './TradingRiskSettings'

const SESSIONS = [1, 3, 5] as const
const SIDES: { v: BandSide; label: string }[] = [
  { v: 'wait', label: 'Wait' },
  { v: 'notify', label: 'Tell me' },
  { v: 'skipDay', label: 'Skip the day' },
  { v: 'pause', label: 'Pause & ask' },
  { v: 'cancel', label: 'Cancel' },
]
const OPEN_MIN = [0, 5, 15, 30] as const
const CLOSE_MIN = [0, 15, 30] as const

/**
 * Defaults for a worker's buy zone ("alım aralığı") — used for whatever a worker's zone doesn't say;
 * every one can be set per worker in chat.
 */
export function BandSettings() {
  const settings = useStore((s) => s.settings)
  const update = useStore((s) => s.updateSettings)
  const b: BandSetting = { ...DEFAULT_BAND, ...(settings?.band ?? {}) }
  const [count, setCount] = useState(b.window.kind === 'sessions' ? String(b.window.count) : '')
  const [cents, setCents] = useState(b.margin.kind === 'usd' ? String(Math.round(b.margin.usd * 100)) : '')
  useEffect(() => setCount(b.window.kind === 'sessions' ? String(b.window.count) : ''), [b.window])
  useEffect(() => setCents(b.margin.kind === 'usd' ? String(Math.round(b.margin.usd * 100)) : ''), [b.margin])
  if (!settings) return null
  const save = (patch: Partial<BandSetting>) => void update({ band: { ...b, ...patch } })
  const saveCount = () => {
    const n = Math.round(Number(count))
    if (Number.isFinite(n) && n >= 1 && n <= 60) save({ window: { kind: 'sessions', count: n } })
    else setCount(b.window.kind === 'sessions' ? String(b.window.count) : '')
  }
  const saveCents = () => {
    const n = Math.round(Number(cents))
    if (Number.isFinite(n) && n >= 1 && n <= 500) save({ margin: { kind: 'usd', usd: n / 100 } })
    else setCents(b.margin.kind === 'usd' ? String(Math.round(b.margin.usd * 100)) : '')
  }
  const seg = (active: boolean) =>
    cn('rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors', active ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg')
  const input = 'w-14 rounded-md border border-[var(--line-soft)] bg-transparent px-2 py-1 text-right text-[12.5px] text-fg tabular-nums'
  const row = 'flex flex-wrap items-center justify-between gap-2 text-[12px]'
  const sideRow = (label: string, hint: string, value: BandSide, key: 'below' | 'above') => (
    <div className={row}>
      <span className="text-fg-muted">
        {label}
        <span className="block text-[11px] text-fg-faint">{hint}</span>
      </span>
      <div className="inline-flex flex-wrap rounded-lg border border-[var(--line-soft)] p-0.5">
        {SIDES.map((x) => (
          <button key={x.v} onClick={() => save({ [key]: x.v } as Partial<BandSetting>)} className={seg(value === x.v)}>
            {x.label}
          </button>
        ))}
      </div>
    </div>
  )
  return (
    <section id="settings-band" className="card p-4">
      <h2 className="text-[14px] font-semibold text-fg">Buy zone</h2>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        The default for a worker that buys in a zone (“100.32–101.00 arası al”) — change any of it for a single worker in chat. The engine watches the
        price live and sends the orders to IBKR only while it is inside the zone.
      </p>

      <div className="mt-3 space-y-3 rounded-lg border border-[var(--line-soft)] p-3">
        <div className={row}>
          <span className="text-fg-muted">
            How long it watches
            <span className="block text-[11px] text-fg-faint">Regular sessions, counted from when the worker starts.</span>
          </span>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
              {SESSIONS.map((n) => (
                <button key={n} onClick={() => save({ window: { kind: 'sessions', count: n } })} className={seg(b.window.kind === 'sessions' && b.window.count === n)}>
                  {n}
                </button>
              ))}
              <button onClick={() => save({ window: { kind: 'gtc' } })} className={seg(b.window.kind === 'gtc')}>
                until stopped
              </button>
            </div>
            <label className="flex items-center gap-1.5 text-fg-subtle">
              <input
                inputMode="numeric"
                value={count}
                placeholder="—"
                onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
                onBlur={saveCount}
                onKeyDown={(e) => e.key === 'Enter' && saveCount()}
                className={input}
              />
              sessions
            </label>
          </div>
        </div>
        {sideRow('Under the zone', 'Orders are pulled there, so nothing is bought under it.', b.below, 'below')}
        {sideRow('Over the zone', 'The order stays but can only fill back inside; it never chases.', b.above, 'above')}
        <div className={row}>
          <span className="text-fg-muted">
            Limit margin
            <span className="block text-[11px] text-fg-faint">The buy limit is the price plus this, never over the zone’s top.</span>
          </span>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
              <button onClick={() => save({ margin: { kind: 'auto' } })} className={seg(b.margin.kind === 'auto')}>
                auto (⅓ of the 5-min noise)
              </button>
            </div>
            <label className="flex items-center gap-1.5 text-fg-subtle">
              <input
                inputMode="numeric"
                value={cents}
                placeholder="—"
                onChange={(e) => setCents(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                onBlur={saveCents}
                onKeyDown={(e) => e.key === 'Enter' && saveCents()}
                className={input}
              />
              ¢
            </label>
          </div>
        </div>
        <div className={row}>
          <span className="text-fg-muted">Not in the first minutes after the open</span>
          <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
            {OPEN_MIN.map((n) => (
              <button key={n} onClick={() => save({ skipOpenMin: n })} className={seg(b.skipOpenMin === n)}>
                {n ? `${n} min` : 'off'}
              </button>
            ))}
          </div>
        </div>
        <div className={row}>
          <span className="text-fg-muted">No new orders before the close</span>
          <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
            {CLOSE_MIN.map((n) => (
              <button key={n} onClick={() => save({ stopBeforeCloseMin: n })} className={seg(b.stopBeforeCloseMin === n)}>
                {n ? `last ${n} min` : 'off'}
              </button>
            ))}
          </div>
        </div>
        <div className={row}>
          <span className="text-fg-muted">
            Confirm on a 5-minute close
            <span className="block text-[11px] text-fg-faint">Cancelling and notices wait for a 5-minute close outside the zone — a wick doesn’t count.</span>
          </span>
          <Switch on={b.confirm === '5m'} label="5-minute confirmation" onChange={(v) => save({ confirm: v ? '5m' : 'live' })} />
        </div>
        <div className={row}>
          <span className="text-fg-muted">
            Pre-market gate
            <span className="block text-[11px] text-fg-faint">The last pre-market trade decides the day (thesis level, the sides, a no-chase level). Orders stay in regular hours.</span>
          </span>
          <Switch on={b.premarket} label="Pre-market gate" onChange={(v) => save({ premarket: v })} />
        </div>
        <div className={row}>
          <span className="text-fg-muted">
            End-of-day note
            <span className="block text-[11px] text-fg-faint">After a close without a buy: the price and the sessions left.</span>
          </span>
          <Switch on={b.dailyNote} label="End-of-day note" onChange={(v) => save({ dailyNote: v })} />
        </div>
      </div>
      <p className="mt-2.5 text-[11.5px] text-fg-faint">
        A thesis level (“99.50’nin altına düşerse hiç girme”) and a no-chase level (“102.20’yi görürse o gün kovalama”) are set per worker in chat.
        Acting on time needs real-time IBKR data; with delayed data the zone reacts late.
      </p>
    </section>
  )
}
